import { defineStore } from 'pinia'

export const ModelTableStore = defineStore('modelTable', {
  state: () => {
    return {
      /**
       * The name of the model for which the store will be used.
       * Acceptable values: sites, lci_components.
       */
      modelName: null,
      /**
       * Persists the state of the sites table, e.g. selected dropdown filter,
       * or selected custom filters.
       * @note The sites list state might be used either from the users owned
       * sites or from a client's managed account. In that case shall we persist
       * the state for all sites list or only the one that is active? Let's start
       * with the simple approach (if the user opens a sites list then the other
       * will be reset) and if we see that it is required (e.g. the user has
       * two tabs open) we will scope each state bellow an identifier. E.g.
       * tableState = { ownedSites: { ... state ...}, clientsSites: { ... state ...} }
       */
      tableState: {}
    }
  },
  getters: {
    /**
     * Returns the value of the provided key from the sites
     * list state.
     */
    getTableFilterValue: (state) => {
      return (key) => state.tableState[key]
    },
    /**
     * Returns an object that represents the initial state of the table,
     * based on the stored state. Used to render the table in a filtered
     * state.
     * @param state
     */
    getTableInitialState: (state) => {
      const initialState = {}

      const customFiltersValue = state.tableState.customFilters
      if (customFiltersValue) {
        const isNonEmptyObject = Object.keys(customFiltersValue).length > 0
        if (isNonEmptyObject) {
          initialState.customSelectedFilters = customFiltersValue
        }
      }

      const textSearchQuery = state.tableState.textFilter
      if (textSearchQuery !== null) {
        initialState.textSearchQuery = textSearchQuery
      }

      return Object.assign({}, initialState)
    }
  },
  actions: {
    /**
     * Sets the value for a specific key in the sites list state. Also, stores
     * the value in the local storage to persist it after reloads from browser.
     * @param {String} key The key that will be updated.
     * @param value The value that will be stored in the provided key.
     */
    setTableFilterValue(key, value) {
      this.tableState[key] = value
    },
    /**
     * Sets the name of the model state to be stored. Resets the table state
     * to a clean object if the model name changes. Used when we change the
     * model of interest (e.g. a user views their sites, and then they visit
     * the LCI components list). In that case, any existing state should not
     * interfere with the state of the new table.
     * @param {String} modelName The name of the model to be stored.
     */
    setModelName(modelName) {
      if (modelName !== this.modelName) {
        this.modelName = modelName
        this.tableState = {}
      }
    }
  },
  persist: true
})